import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
/**
 * The drawings the insight tabs are made of.
 *
 * Hand-drawn SVG, for the reason `site/src/components/chart.tsx` gives and one
 * more: every chart here is read against a table of the same numbers sitting
 * beside it, so the chart's job is to show a *shape* — is it moving, who is
 * above us — and the table's job is to be exact. A charting library would add
 * more bytes than the whole panel to do the second job twice.
 *
 * Three rules the whole file follows.
 *
 * The axis includes zero, always. A truncated axis turns two points into a
 * cliff, and these are numbers customers put in board decks.
 *
 * Nothing measured is never a zero. `null` breaks the line and prints an em
 * dash; a brand that was not sampled must not look like a brand that scored
 * nothing.
 *
 * Colour is the second channel, never the only one. Five series is the ceiling
 * because the palette has five encodings of quantity and inventing a sixth
 * colour in a component file is the bug `packages/tokens` exists to prevent —
 * the standings table beside the chart is where the other seven brands live.
 */

import { Download } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { AssetValue, MetricBlock, MetricName, SeriesPoint } from "~/core/api/index";
import { isPercent, lowerIsBetter } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { Hint } from "~/core/ui/page";
import { download } from "~/modules/analytics/components/table";

/** The encodings of quantity, in the order the tokens define them. */
const PALETTE = ["var(--v-1)", "var(--v-2)", "var(--v-3)", "var(--v-4)", "var(--v-5)"];

/** How many brands a chart draws before the table beside it takes over. */
const SERIES_LIMIT = 5;

// ---------------------------------------------------------------------------
// Formatting a metric
// ---------------------------------------------------------------------------

/**
 * One value, in the unit its metric is measured in.
 *
 * The em dash is load-bearing and appears in exactly one place, here, so no
 * screen can decide on its own that unmeasured means zero.
 */
export function value(locale: "en" | "zh", metric: MetricName, n: number | null | undefined): string {
	if (n === null || n === undefined) return "—";
	return isPercent(metric) ? fmt.percent(locale, n, 1) : n.toFixed(1);
}

/**
 * The floors under a reported movement: usable answers in each window, and the
 * size of the move in points. The overview states the same two numbers in its
 * own sentence, and a reading that drew an arrow on less would be contradicting
 * the screen beside it.
 */
const MIN_SAMPLES = 5;
const MIN_MOVE = 20;

/**
 * The movement against the window before this one.
 *
 * Direction, not sign: a position that fell from 4.0 to 2.0 is a −2.0 and an
 * improvement, and an arrow that pointed down would say the opposite of what
 * happened. Nothing is drawn when either window is unmeasured, and "no
 * conclusion yet" is drawn when either window is thin or the move is small —
 * the honest reading, and the one the overview gives for the same numbers.
 *
 * The sample counts come with the block where the server counted them, or
 * from the tab where the tab did; a caller's explicit count wins.
 */
export function Delta({
	block,
	samples,
	samplesPrevious,
}: {
	block: Pick<MetricBlock, "metric" | "value" | "previous" | "samples" | "samples_previous">;
	samples?: number;
	samplesPrevious?: number;
}) {
	const { locale } = useI18n();
	const t = useT();
	if (block.value === null || block.previous === null) return null;

	const now = samples ?? block.samples;
	const before = samplesPrevious ?? block.samples_previous;
	const thin = (now !== undefined && now < MIN_SAMPLES) || (before !== undefined && before < MIN_SAMPLES);
	if (thin) return <span className="delta delta-flat">{t("overview.noConclusion")}</span>;

	const delta = block.value - block.previous;
	if (Math.abs(delta) < 0.05) return <span className="delta delta-flat">{t("insights.flat")}</span>;
	// Position is a rank, not points, and has no noise floor in points.
	if (block.metric !== "average_position" && Math.abs(delta) < MIN_MOVE) {
		return <span className="delta delta-flat">{t("overview.noConclusion")}</span>;
	}

	const better = lowerIsBetter(block.metric) ? delta < 0 : delta > 0;
	const shown = isPercent(block.metric)
		? t("insights.pp", { n: fmt.signed(locale, delta, 1) })
		: fmt.signed(locale, delta, 1);
	return (
		<span className={better ? "delta delta-up" : "delta delta-down"}>
			{delta > 0 ? "▲" : "▼"} {shown}
		</span>
	);
}

// ---------------------------------------------------------------------------
// The metric block
// ---------------------------------------------------------------------------

/**
 * The shape every insight tab repeats: a chart on the left, the standings on
 * the right.
 *
 * It is one component rather than a layout each screen assembles, because the
 * two halves have to be read together — the chart says "we are climbing", the
 * table says "and still fourth" — and a screen that showed one without the
 * other would be answering half the question.
 */
export function MetricPanel({
	metric,
	title,
	lede,
	block,
	chart,
	rankLabel,
	samples,
	samplesPrevious,
}: {
	metric: MetricName;
	title: string;
	lede: string;
	block: MetricBlock;
	/** The left half. A line for a window, a donut for a share, bars for an axis. */
	chart: React.ReactNode;
	/** What the right-hand column is a ranking of: brands, platforms, topics. */
	rankLabel: string;
	/** The answers behind the figure, where the tab rather than the block counts them. */
	samples?: number;
	samplesPrevious?: number;
}) {
	const { locale } = useI18n();
	const t = useT();
	const [expanded, setExpanded] = useState(false);
	const rows = expanded ? block.assets : block.assets.slice(0, 5);

	return (
		<section className="section">
			<div className="section-head">
				<div className="title-row">
					<h2>{title}</h2>
					<Hint text={lede} />
				</div>
			</div>

			<div className="metric-panel">
				<div className="metric-figure">
					<div className="metric-head">
						<span className="label">{title}</span>
						<span className="value num">{value(locale, metric, block.value)}</span>
						<Delta block={block} samples={samples} samplesPrevious={samplesPrevious} />
						{/* The chart is also "what is on screen", and this is the
						    numbers behind it: the standings and every point of the
						    series, in the same file, so a customer can check the shape
						    they are about to put in a deck. */}
						<Button
							variant="default"
							className="metric-download"
							type="button"
							title={t("table.export")}
							aria-label={t("table.export")}
							onClick={() => download(`${metric}`, metricCsv(block, rankLabel))}
						>
							<Download aria-hidden />
						</Button>
					</div>
					{chart}
				</div>

				<div className="metric-rank">
					<div className="metric-head">
						<span className="label">{t("insights.rank")}</span>
						<span className="value num">
							{block.rank === null ? "—" : block.rank}
							{block.rank === null ? null : <span className="faint"> / {block.ranked}</span>}
						</span>
					</div>

					<Table variant="resource" className="rank-table">
						<TableHeader>
							<TableRow>
								<TableHead className="wrap">{rankLabel}</TableHead>
								<TableHead className="num">{title}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows.length === 0 ? (
								<TableRow>
									<TableCell className="wrap faint" colSpan={2}>
										{t("insights.noData")}
									</TableCell>
								</TableRow>
							) : (
								rows.map((row) => <RankRow key={row.entity_id} row={row} metric={metric} />)
							)}
						</TableBody>
					</Table>

					{block.assets.length > 5 ? (
						<Button variant="default" type="button" onClick={() => setExpanded(!expanded)}>
							{expanded ? t("insights.collapse") : t("insights.expand")}
						</Button>
					) : null}
				</div>
			</div>
		</section>
	);
}

/**
 * The block's numbers as rows: the standings first, then the series.
 *
 * One file rather than two, because they are one reading — the table says where
 * everybody stands and the series says how they got there, and a customer
 * checking a chart wants both in the sheet they open.
 */
export function metricCsv(block: MetricBlock, rankLabel: string): (string | number)[][] {
	const names = new Map(block.assets.map((asset) => [asset.entity_id, asset.name]));
	return [
		[rankLabel, "rank", block.metric, "previous"],
		...block.assets.map((asset) => [
			asset.name,
			asset.rank ?? "",
			asset.value?.toFixed(2) ?? "",
			asset.previous?.toFixed(2) ?? "",
		]),
		[],
		["bucket", rankLabel, block.metric],
		...block.series.map((point) => [
			point.bucket,
			names.get(point.entity_id) ?? point.entity_id,
			point.value?.toFixed(2) ?? "",
		]),
	];
}

function RankRow({ row, metric }: { row: AssetValue; metric: MetricName }) {
	const { locale } = useI18n();
	return (
		<TableRow className={row.is_you ? "is-you" : undefined}>
			<TableCell className="wrap">
				<span className="rank-place num">{row.rank ?? "—"}</span>
				{row.name}
			</TableCell>
			<TableCell className="num mono">{value(locale, metric, row.value)}</TableCell>
		</TableRow>
	);
}

// ---------------------------------------------------------------------------
// The time series
// ---------------------------------------------------------------------------

/**
 * One metric over the window, one line per brand.
 *
 * The x axis is the buckets that exist, evenly spaced, rather than a calendar:
 * this product samples weekly on most plans, and a calendar axis would draw six
 * days of empty space between every pair of points and read as an outage.
 */
export function Series({
	metric,
	points,
	assets,
	distinguishLines = false,
}: {
	metric: MetricName;
	points: SeriesPoint[];
	assets: AssetValue[];
	distinguishLines?: boolean;
}) {
	const id = useId();
	const { locale } = useI18n();
	const t = useT();
	const [at, setAt] = useState<number | null>(null);

	const buckets = [...new Set(points.map((point) => point.bucket))].sort();
	const shown = assets.slice(0, SERIES_LIMIT);
	const hasData =
		buckets.length > 0 &&
		shown.length > 0 &&
		points.some((point) => point.value !== null && shown.some((asset) => asset.entity_id === point.entity_id));
	const svgRef = useRef<SVGSVGElement>(null);
	const [width, setWidth] = useState(720);
	useEffect(() => {
		if (!hasData || !svgRef.current) return;
		// Match plot coordinates to CSS pixels so narrow cards do not squeeze
		// axis labels and round points into unreadable slivers.
		const observer = new ResizeObserver(([entry]) => {
			if (entry.contentRect.width > 0) setWidth(entry.contentRect.width);
		});
		observer.observe(svgRef.current);
		return () => observer.disconnect();
	}, [hasData]);
	if (!hasData) return <p className="empty">{t("insights.noData")}</p>;

	const byKey = new Map(points.map((point) => [`${point.bucket}\u0000${point.entity_id}`, point.value]));
	const series = shown.map((asset) => ({
		asset,
		values: buckets.map((bucket) => byKey.get(`${bucket}\u0000${asset.entity_id}`) ?? null),
	}));

	const height = 220;
	const pad = { top: 12, right: 12, bottom: 26, left: 44 };
	const plotW = width - pad.left - pad.right;
	const plotH = height - pad.top - pad.bottom;

	const measured = series.flatMap((line) => line.values).filter((n): n is number => n !== null);
	// Position counts down from 1 and has no natural ceiling; everything else is
	// a percentage and gets a floor of 10 so a flat 2% is not drawn as a mountain.
	// Sentiment runs from −100 to 100, and is drawn either side of a zero line:
	// a scale that started at zero would put every negative reading off the
	// chart, or on it and looking positive.
	const signed = metric === "sentiment";
	const max = signed
		? Math.max(10, ...measured.map(Math.abs))
		: isPercent(metric)
			? Math.max(10, ...measured)
			: Math.max(3, ...measured);
	const min = signed ? -max : 0;
	const ticks = signed ? [min, 0, max] : [0, max / 2, max];
	const x = (i: number) => pad.left + (buckets.length > 1 ? (i * plotW) / (buckets.length - 1) : plotW / 2);
	const y = (n: number) => pad.top + plotH - ((n - min) / (max - min)) * plotH;

	return (
		<>
			<svg
				className="series"
				ref={svgRef}
				viewBox={`0 0 ${width} ${height}`}
				preserveAspectRatio="none"
				role="img"
				aria-labelledby={`${id}-title`}
				onMouseLeave={() => setAt(null)}
			>
				<title id={`${id}-title`}>{t("insights.seriesAlt")}</title>

				{ticks.map((tick) => (
					<g key={tick}>
						{/* The zero line on a signed scale is the reading — above it
						    is liked, below it is not — so it is drawn in the stronger
						    border rather than as one more gridline. */}
						<line
							className="spark-grid"
							stroke={signed && tick === 0 ? "var(--border-strong)" : undefined}
							x1={pad.left}
							x2={width - pad.right}
							y1={y(tick)}
							y2={y(tick)}
						/>
						<text className="spark-tick" x={pad.left - 6} y={y(tick) + 3} textAnchor="end">
							{isPercent(metric) ? `${Math.round(tick)}%` : tick.toFixed(1)}
						</text>
					</g>
				))}

				{series.map((line, index) => (
					// One path per run of measured points, so a gap in the middle
					// is a gap and not a straight line drawn through nothing.
					<g key={line.asset.entity_id}>
						{runs(line.values).map(([from, to]) =>
							// A run of one is a dot, drawn explicitly. A zero-length
							// path with a round cap is a dot in theory and about one
							// pixel of it here, which is the whole chart in the first
							// week of every account: one firing, one bucket, and a
							// panel that looks like it measured nothing.
							to - from === 1 ? (
								<circle
									key={from}
									className="series-point"
									fill={PALETTE[index % PALETTE.length]}
									cx={x(from)}
									cy={y(line.values[from] as number)}
									r={3}
								/>
							) : (
								<path
									key={from}
									className="series-line"
									strokeDasharray={distinguishLines && index > 0 ? `${6 + index * 2} 4` : undefined}
									stroke={PALETTE[index % PALETTE.length]}
									d={line.values
										.slice(from, to)
										.map((n, i) => `${i === 0 ? "M" : "L"}${x(from + i).toFixed(1)},${y(n as number).toFixed(1)}`)
										.join(" ")}
								/>
							),
						)}
					</g>
				))}

				{/* One hit target per bucket, full height: a reader aiming at a
				    2px line on a phone hits nothing. Focusable as well as
				    hoverable, so the readout is reachable from the keyboard —
				    otherwise the exact numbers are mouse-only, and the table
				    beside the chart is the only way to get at them. */}
				{buckets.map((bucket, i) => (
					// biome-ignore lint/a11y/useSemanticElements: a <button> cannot exist inside <svg>, and the readout has to be keyboard reachable
					<rect
						key={bucket}
						role="button"
						tabIndex={0}
						aria-label={bucket}
						x={x(i) - plotW / Math.max(buckets.length, 1) / 2}
						y={pad.top}
						width={plotW / Math.max(buckets.length, 1)}
						height={plotH}
						fill="transparent"
						onMouseEnter={() => setAt(i)}
						onFocus={() => setAt(i)}
					/>
				))}

				{at !== null ? (
					<line className="series-cursor" x1={x(at)} x2={x(at)} y1={pad.top} y2={pad.top + plotH} />
				) : null}

				{/* The first bucket and the last. One bucket is *both*, so the
				    pair is deduplicated — a window with a single day in it would
				    otherwise draw the same label twice, on top of itself. */}
				{[...new Set([0, buckets.length - 1])].map((i, n) =>
					buckets[i] ? (
						<text
							key={buckets[i]}
							className="spark-tick"
							x={x(i)}
							y={height - 8}
							textAnchor={n === 0 ? "start" : "end"}
						>
							{buckets[i]}
						</text>
					) : null,
				)}
			</svg>

			<div className="series-legend">
				{series.map((line, index) => (
					<span key={line.asset.entity_id} className={line.asset.is_you ? "legend is-you" : "legend"}>
						<span className="swatch" style={{ background: PALETTE[index % PALETTE.length] }} />
						{line.asset.name}
						{at !== null ? <span className="mono faint"> {value(locale, metric, line.values[at])}</span> : null}
					</span>
				))}
				{at !== null ? <span className="faint mono">{buckets[at]}</span> : null}
			</div>
		</>
	);
}

/**
 * The `[start, end)` spans of consecutive measured points.
 *
 * A span of one is legal and is drawn as a dot: a brand measured in exactly one
 * bucket of the window has a reading, and a chart that showed nothing for it
 * would be reporting an absence it did not observe.
 */
function runs(values: (number | null)[]): [number, number][] {
	const spans: [number, number][] = [];
	let start: number | null = null;
	values.forEach((n, i) => {
		if (n !== null && start === null) start = i;
		if (n === null && start !== null) {
			// A single point is a run of one and still worth drawing — as a dot
			// it would be lost, so it is drawn as a zero-length path with a round
			// cap, which the stylesheet gives it.
			spans.push([start, i]);
			start = null;
		}
	});
	if (start !== null) spans.push([start, values.length]);
	return spans;
}

// ---------------------------------------------------------------------------
// The donut
// ---------------------------------------------------------------------------

/**
 * Share of voice, as the one chart where the parts have to add to the whole.
 *
 * The remainder is drawn and labelled rather than dropped. A donut of the top
 * five brands that fills the ring is a donut claiming those five are the
 * market, and on most of these questions they are about a third of it.
 */
export function Donut({ assets }: { assets: AssetValue[] }) {
	const { locale } = useI18n();
	const t = useT();
	const shown = assets.filter((asset) => asset.value !== null).slice(0, SERIES_LIMIT);
	const total = assets.reduce((sum, asset) => sum + (asset.value ?? 0), 0);
	if (shown.length === 0) return <p className="empty">{t("insights.noData")}</p>;

	const rest = Math.max(0, 100 - total);
	// Keyed by id, not by name: two listings in the directory can carry the same
	// name — a brand imported twice under two slugs — and a name key silently
	// drops one of the two slices while the ring still adds up to a hundred.
	const slices = [
		...shown.map((asset, i) => ({
			id: asset.entity_id,
			name: asset.name,
			n: asset.value ?? 0,
			fill: PALETTE[i % PALETTE.length],
		})),
	];
	const drawn = slices.reduce((sum, slice) => sum + slice.n, 0);
	const other = 100 - drawn;
	if (other > 0.05) {
		// The panel's neutral rule colour. This read the Agent's `--surface-2`,
		// which is the card's own white, so the remainder and its legend swatch
		// were drawn in the colour of what they sat on.
		slices.push({ id: "other", name: t("insights.other"), n: other, fill: "var(--border)" });
	}

	const size = 220;
	const radius = 88;
	const stroke = 26;
	const circumference = 2 * Math.PI * radius;
	let offset = 0;

	return (
		<div className="donut-wrap">
			<svg className="donut" viewBox={`0 0 ${size} ${size}`} role="img">
				<title>{slices.map((slice) => `${slice.name}: ${fmt.percent(locale, slice.n, 1)}`).join(", ")}</title>
				{slices.map((slice) => {
					const length = (slice.n / 100) * circumference;
					const dash = `${length} ${circumference - length}`;
					const element = (
						<circle
							key={slice.id}
							cx={size / 2}
							cy={size / 2}
							r={radius}
							fill="none"
							stroke={slice.fill}
							strokeWidth={stroke}
							strokeDasharray={dash}
							strokeDashoffset={-offset}
							// Start at twelve o'clock: a ring that starts at three
							// reads as having been rotated by mistake.
							transform={`rotate(-90 ${size / 2} ${size / 2})`}
						/>
					);
					offset += length;
					return element;
				})}
			</svg>

			<div className="donut-legend">
				{slices.map((slice) => (
					<span key={slice.id} className="legend">
						<span className="swatch" style={{ background: slice.fill }} />
						{slice.name}
						<span className="mono faint"> {fmt.percent(locale, slice.n, 1)}</span>
					</span>
				))}
				{rest > 99.9 ? <span className="faint">{t("insights.noData")}</span> : null}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Bars along an axis
// ---------------------------------------------------------------------------

/**
 * One metric across a small, named axis: platforms, topics, personas.
 *
 * Horizontal, because the labels are words — "通义千问 Qwen", "Marketing
 * Manager" — and vertical bars would either truncate them or turn them
 * sideways. The number is printed at the end of every bar: the bar is for
 * comparing, the number is for quoting.
 */
export function Bars({
	metric,
	rows,
}: {
	metric: MetricName;
	rows: { key: string; label?: string; value: number | null; previous?: number | null }[];
}) {
	const { locale } = useI18n();
	const t = useT();
	const measured = rows.map((row) => row.value).filter((n): n is number => n !== null);
	if (measured.length === 0) return <p className="empty">{t("insights.noData")}</p>;

	// The bar encodes *standing*, and standing is not the raw number on the one
	// metric where smaller is better: drawing position proportionally would give
	// the worst platform the longest bar, the opposite of what a bar says
	// everywhere else on this screen. So position is drawn against the best one
	// — first place fills the row — and the figure beside it is the measurement,
	// unchanged. Everything else is scaled against the largest bar rather than
	// against 100%, or a metric where every value is under 5% is five invisible
	// slivers.
	const best = Math.min(...measured);
	const max = Math.max(...measured, 1);
	const width = (n: number | null) => {
		if (n === null) return 0;
		const share = lowerIsBetter(metric) ? (n === 0 ? 1 : best / n) : n / max;
		return Math.max(1, Math.min(1, share) * 100);
	};

	// Sentiment is signed, and a signed bar grows from the middle of the row:
	// a −40 drawn from the left edge is a bar as long as a +40, pointing the
	// same way, and the sign is left to the figure beside it.
	const signed = metric === "sentiment";
	const span = signed ? Math.max(10, ...measured.map(Math.abs)) : 0;
	const bar = (n: number | null): React.CSSProperties => {
		if (!signed) return { width: `${width(n)}%` };
		if (n === null) return { width: 0 };
		const half = Math.max(1, (Math.abs(n) / span) * 50);
		return { width: `${half}%`, marginLeft: `${n < 0 ? 50 - half : 50}%` };
	};

	return (
		<ul className="bar-rows">
			{rows.map((row) => (
				<li key={row.key}>
					<span className="bar-label">{row.label ?? row.key}</span>
					<span className="bar">
						<span style={bar(row.value)} />
					</span>
					<span className="num mono">{value(locale, metric, row.value)}</span>
				</li>
			))}
		</ul>
	);
}

// ---------------------------------------------------------------------------
// The priority matrix
// ---------------------------------------------------------------------------

interface MatrixPoint {
	key: string;
	label: string;
	x: number;
	y: number;
	/** Answers behind the point, drawn as its area — a topic measured twice and
	 *  a topic measured two hundred times are not the same finding. */
	weight: number;
}

/**
 * Topics on two axes, with the quadrant each one falls in.
 *
 * The two axes are the two halves of being cited: shown in the answer, and
 * linked from it. A topic high on both is working; high on visibility and low
 * on citations means the assistants know the answer without needing your page,
 * which is a completely different problem from not being in the answer at all.
 * The quadrants are the point — they turn a scatter into four instructions.
 */
export function Matrix({
	points,
	xLabel,
	yLabel,
	quadrants,
}: {
	points: MatrixPoint[];
	xLabel: string;
	yLabel: string;
	/** Bottom-left, bottom-right, top-left, top-right — in reading order of the
	 *  axes rather than of the page, so the caller cannot mis-order them. */
	quadrants: { lowLow: string; lowHigh: string; highLow: string; highHigh: string };
}) {
	const t = useT();
	const { locale } = useI18n();
	const [hover, setHover] = useState<string | null>(null);
	if (points.length === 0) return <p className="empty">{t("insights.noData")}</p>;

	const size = 420;
	const pad = { top: 16, right: 16, bottom: 36, left: 44 };
	const plotW = size - pad.left - pad.right;
	const plotH = size - pad.top - pad.bottom;
	const maxX = Math.max(1, ...points.map((point) => point.x));
	const maxY = Math.max(1, ...points.map((point) => point.y));
	const maxWeight = Math.max(1, ...points.map((point) => point.weight));

	const px = (n: number) => pad.left + (n / maxX) * plotW;
	const py = (n: number) => pad.top + plotH - (n / maxY) * plotH;
	// Area, not radius: a radius proportional to the count makes a topic with
	// four times the evidence look sixteen times as important.
	const r = (weight: number) => 5 + Math.sqrt(weight / maxWeight) * 11;

	return (
		<div className="matrix-wrap">
			<svg className="matrix" viewBox={`0 0 ${size} ${size}`} role="img">
				<title>{`${yLabel} × ${xLabel}`}</title>

				<rect
					x={pad.left}
					y={pad.top}
					width={plotW}
					height={plotH}
					fill="none"
					stroke="var(--border)"
					strokeWidth="1"
				/>
				<line
					className="spark-grid"
					x1={pad.left + plotW / 2}
					x2={pad.left + plotW / 2}
					y1={pad.top}
					y2={pad.top + plotH}
				/>
				<line
					className="spark-grid"
					x1={pad.left}
					x2={pad.left + plotW}
					y1={pad.top + plotH / 2}
					y2={pad.top + plotH / 2}
				/>

				<text className="matrix-quadrant" x={pad.left + 8} y={pad.top + 18}>
					{quadrants.lowHigh}
				</text>
				<text className="matrix-quadrant" x={pad.left + plotW - 8} y={pad.top + 18} textAnchor="end">
					{quadrants.highHigh}
				</text>
				<text className="matrix-quadrant" x={pad.left + 8} y={pad.top + plotH - 8}>
					{quadrants.lowLow}
				</text>
				<text className="matrix-quadrant" x={pad.left + plotW - 8} y={pad.top + plotH - 8} textAnchor="end">
					{quadrants.highLow}
				</text>

				{points.map((point) => (
					// The `<title>` is the accessible name, so the dot is already
					// readable; the hover only cross-highlights it against the list
					// beside the plot, which is where the keyboard path lives.
					// The dot carries its own `<title>`, which is its tooltip and
					// its accessible name. The highlighting is driven from the list
					// beside the plot rather than from here: a dot is a 10px target
					// with no keyboard path, and duplicating the interaction on it
					// would be a second way to do the same thing that only works
					// for people using a mouse.
					<circle
						key={point.key}
						className={hover === point.key ? "matrix-dot is-hover" : "matrix-dot"}
						cx={px(point.x)}
						cy={py(point.y)}
						r={r(point.weight)}
					>
						<title>{`${point.label} — ${yLabel} ${fmt.percent(locale, point.y, 1)}, ${xLabel} ${fmt.percent(locale, point.x, 1)}`}</title>
					</circle>
				))}

				<text className="spark-tick" x={pad.left + plotW / 2} y={size - 8} textAnchor="middle">
					{xLabel}
				</text>
				<text className="spark-tick" x={-(pad.top + plotH / 2)} y={12} textAnchor="middle" transform="rotate(-90)">
					{yLabel}
				</text>
			</svg>

			{/* The labels as a list beside the plot rather than on it: forty topic
			    names drawn at their own points overlap into a grey smear, and the
			    list is also what a reader can search and copy. */}
			<ul className="matrix-key">
				{points.map((point) => (
					<li key={point.key} className={hover === point.key ? "is-hover" : undefined}>
						{/* A button, so the cross-highlight has a keyboard path and the
						    list is not a mouse-only legend. */}
						<Button
							variant="ghost"
							className="w-full justify-between h-auto whitespace-normal text-left"
							type="button"
							onMouseEnter={() => setHover(point.key)}
							onMouseLeave={() => setHover(null)}
							onFocus={() => setHover(point.key)}
							onBlur={() => setHover(null)}
						>
							<span className="wrap">{point.label}</span>
							<span className="mono faint">
								{fmt.percent(locale, point.y, 1)} · {fmt.percent(locale, point.x, 1)}
							</span>
						</Button>
					</li>
				))}
			</ul>
		</div>
	);
}
